import React, { useEffect, useCallback } from 'react';
import { X, Mail } from 'lucide-react';

interface AboutUsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AboutUsModal: React.FC<AboutUsModalProps> = ({ isOpen, onClose }) => {
  const handleEscape = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleEscape]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div 
            className="relative w-full max-w-2xl rounded-xl bg-white shadow-2xl transform transition-all"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-500 focus:outline-none transition-colors duration-200"
              aria-label="Close modal"
            >
              <X className="h-6 w-6" />
            </button>

            {/* Content */}
            <div className="p-6">
              <h2 id="modal-title" className="text-3xl font-bold text-gray-900 mb-6">
                About Us
              </h2>
              
              <div className="space-y-6 text-gray-600">
                <p className="leading-relaxed">
                  At Aqua Blue Cyprus, we are passionate about delivering top qugitnality swimming pool services in Paphos, Cyprus. With over 20 years of experience, we specialize in pool maintenance, repairs, construction, and water treatment ensuring every pool stays in perfect condition, all year round.
                </p>
                
                <p className="leading-relaxed">
                  Founded by a seasoned pool technician with a deep understanding of pool systems, our company is built on expertise, reliability, and customer satisfaction. From private villas to hotels and complexes, we provide custom solutions tailored to every client's needs.
                </p>

                <div className="bg-cyan-50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Why Choose Us?</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <span className="text-cyan-500 mr-2">•</span>
                      <span>Decades of Experience – 20+ years in pool engineering & maintenance.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-cyan-500 mr-2">•</span>
                      <span>Quality & Professionalism – We use top-grade equipment & eco-friendly treatments.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-cyan-500 mr-2">•</span>
                      <span>Local Experts in Paphos – We know the unique pool needs of Cyprus.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-cyan-500 mr-2">•</span>
                      <span>Customer-First Approach – Personalized service with fast response times.</span>
                    </li>
                  </ul>
                </div>

                <p className="leading-relaxed">
                  Whether you need routine cleaning, urgent repairs, or a brand-new pool installation, we are here to help. Let's keep your pool crystal clear and running smoothly!
                </p>

                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <a
                    href="mailto:info@aquabluecyprus.com"
                    className="flex items-center gap-2 text-cyan-600 hover:text-cyan-700 transition-colors duration-200 group"
                  >
                    <Mail className="h-5 w-5" />
                    <span className="font-semibold group-hover:underline">
                      Get in touch today!
                    </span>
                  </a>
                  <button
                    onClick={onClose}
                    className="px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors duration-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AboutUsModal; 